import * as React from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import {
  accountSettingsFieldSelectContentClassName,
  accountSettingsFieldSelectItemClassName,
  accountSettingsFieldSelectTriggerClassName,
} from "../accountSettingsCardShell";
import {
  useInstrumentSetupSelectOpenNotifier,
  useInstrumentSetupSelectPortal,
} from "@/components/Instruments/InstrumentSetupSelectPortal";

export type AccountSettingsFieldSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type AccountSettingsFieldSelectProps = {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: AccountSettingsFieldSelectOption[];
  disabled?: boolean;
  placeholder?: string;
  menuPortalContainer?: HTMLElement | null;
  onOpenChange?: (open: boolean) => void;
};

export function AccountSettingsFieldSelect({
  id,
  value,
  onValueChange,
  options,
  disabled = false,
  placeholder,
  menuPortalContainer,
  onOpenChange,
}: AccountSettingsFieldSelectProps): React.ReactElement {
  const setupPortal = useInstrumentSetupSelectPortal();
  const notifySetupSelectOpen = useInstrumentSetupSelectOpenNotifier();
  const portalContainer = menuPortalContainer ?? setupPortal ?? undefined;
  // Radix Select treats "" as invalid for controlled `value` — selection won't stick.
  const selectValue = value.trim() ? value : undefined;

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      notifySetupSelectOpen(open);
    },
    [notifySetupSelectOpen, onOpenChange]
  );

  return (
    <div data-field-select="" className="w-full min-w-0">
      <Select
        value={selectValue}
        onValueChange={onValueChange}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        className="w-full"
      >
        <SelectTrigger
          id={id}
          data-account-settings-field-select=""
          className={accountSettingsFieldSelectTriggerClassName}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent
          data-account-settings-field-select-content=""
          position="popper"
          sideOffset={4}
          align="start"
          portalContainer={portalContainer}
          className={accountSettingsFieldSelectContentClassName}
        >
          <SelectGroup>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  accountSettingsFieldSelectItemClassName,
                  option.disabled && "cursor-not-allowed opacity-45",
                )}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}
